import React, { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Checkbox,
  Grid,
  TextField,
  Chip,
} from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./css/StatsPage.css"; // Import custom CSS if any
import config from "./config";

const devLog = (...args) => {
  if (config.IS_DEV) {
    console.log(...args);
  }
};

const API_URL =
  "https://lykns00a87.execute-api.us-east-2.amazonaws.com/default/football_app_data_crud";

/** Colors for the PieChart arcs */
const pieColors = ["#8884d8", "#82ca9d", "#ffc658", "#ff8042", "#d0ed57"];

// 🚀 NEW: Season Helper Functions
const getSeasonFromDate = (dateString) => {
  const date = new Date(dateString);
  const month = date.getMonth() + 1; // getMonth() returns 0-11
  const year = date.getFullYear();

  // If month is August (8) or later, season starts this year
  // If month is before August, season started last year
  if (month >= 8) {
    return {
      startYear: year,
      endYear: year + 1,
      label: `${year}/${(year + 1).toString().slice(-2)}`,
      startDate: new Date(year, 7, 1), // August 1st (month 7 = August)
      endDate: new Date(year + 1, 6, 31), // July 31st next year (month 6 = July)
    };
  } else {
    return {
      startYear: year - 1,
      endYear: year,
      label: `${year - 1}/${year.toString().slice(-2)}`,
      startDate: new Date(year - 1, 7, 1), // August 1st previous year
      endDate: new Date(year, 6, 31), // July 31st this year
    };
  }
};

const getAvailableSeasons = (gamesData) => {
  const seasonSet = new Set();

  gamesData.forEach((game) => {
    const season = getSeasonFromDate(game.match_date);
    const seasonKey = `${season.startYear}-${season.endYear}`;
    seasonSet.add(seasonKey);
  });

  // Convert to array and sort by start year (most recent first)
  const seasons = Array.from(seasonSet).map((seasonKey) => {
    const [startYear, endYear] = seasonKey.split("-").map(Number);
    return {
      startYear,
      endYear,
      label: `${startYear}/${endYear.toString().slice(-2)}`,
      value: seasonKey,
      startDate: new Date(startYear, 7, 1),
      endDate: new Date(endYear, 6, 31),
    };
  });

  // Sort by start year descending (most recent first)
  seasons.sort((a, b) => b.startYear - a.startYear);

  return seasons;
};

const filterGamesBySeason = (games, selectedSeason) => {
  if (!selectedSeason || selectedSeason === "all") {
    return games;
  }

  const [startYear, endYear] = selectedSeason.split("-").map(Number);
  const seasonStart = new Date(startYear, 7, 1); // August 1st
  const seasonEnd = new Date(endYear, 6, 31, 23, 59, 59); // July 31st end of day

  return games.filter((game) => {
    const gameDate = new Date(game.match_date);
    return gameDate >= seasonStart && gameDate <= seasonEnd;
  });
};

const StatsPage = ({ coach_id }) => {
  // ------------------------- States ------------------------- //
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [viewMode, setViewMode] = useState("team"); // "team" or "individual"
  const [players, setPlayers] = useState([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState("");
  const [gamesData, setGamesData] = useState([]);
  const [aggregatedPlayerStats, setAggregatedPlayerStats] = useState({});
  const [matchesFilter, setMatchesFilter] = useState(1);
  const [chartData, setChartData] = useState([]);
  const [teamInsights, setTeamInsights] = useState({});
  const [playerInsights, setPlayerInsights] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedGameIds, setSelectedGameIds] = useState([]);
  const [topDefensivePlayers, setTopDefensivePlayers] = useState([]);

  // 🚀 NEW: Season Filter States
  const [availableSeasons, setAvailableSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState("all");
  const [seasonFilteredGames, setSeasonFilteredGames] = useState([]);

  // States for Progress Bars
  const [savesPercent, setSavesPercent] = useState(0);
  const [goalsConcededPercent, setGoalsConcededPercent] = useState(0);
  const [savesCount, setSavesCount] = useState(0);
  const [goalsConceded, setGoalsConceded] = useState(0);

  const [shotsOnPercent, setShotsOnPercent] = useState(0);
  const [shotsOffPercent, setShotsOffPercent] = useState(0);
  const [shotsOnCount, setShotsOnCount] = useState(0);
  const [shotsOffCount, setShotsOffCount] = useState(0);

  const [goalsPercent, setGoalsPercent] = useState(0);
  const [assistsPercent, setAssistsPercent] = useState(0);
  const [goalsCount, setGoalsCount] = useState(0);
  const [assistsCount, setAssistsCount] = useState(0);

  // New State Variables for Top Scorers and Assist Providers
  const [topGoalScorers, setTopGoalScorers] = useState([]);
  const [topAssistProviders, setTopAssistProviders] = useState([]);

  // New State for Goals vs. Assists Pie Chart
  const [goalsAssistsPieData, setGoalsAssistsPieData] = useState([]);

  // ------------------------- NEW STATES FOR DATE FILTER ------------------------- //
  const [isDateRange, setIsDateRange] = useState(false); // Tracks if "Between dates..." is selected
  const [startDate, setStartDate] = useState(null); // Start Date
  const [endDate, setEndDate] = useState(null); // End Date

  // ------------------------- Effects ------------------------- //
  useEffect(() => {
    if (coach_id) {
      fetchTeams(coach_id);
    }
  }, [coach_id]);

  useEffect(() => {
    if (selectedTeamId) {
      fetchData(selectedTeamId);
    }
  }, [selectedTeamId]);

  // 🚀 NEW: Process seasons when games data changes
  useEffect(() => {
    if (gamesData.length > 0) {
      const seasons = getAvailableSeasons(gamesData);
      devLog("Available seasons:", seasons);
      setAvailableSeasons(seasons);

      // Auto-select current season if available
      if (seasons.length > 0 && selectedSeason === "all") {
        const currentDate = new Date();
        const currentSeason = getSeasonFromDate(currentDate);
        const currentSeasonKey = `${currentSeason.startYear}-${currentSeason.endYear}`;

        // Check if current season has data
        const hasCurrentSeasonData = seasons.find(
          (s) => s.value === currentSeasonKey
        );
        if (hasCurrentSeasonData) {
          devLog("Auto-selecting current season:", currentSeasonKey);
          setSelectedSeason(currentSeasonKey);
        } else {
          // Select most recent season with data
          devLog("Auto-selecting most recent season:", seasons[0].value);
          setSelectedSeason(seasons[0].value);
        }
      }
    }
  }, [gamesData]);

  // 🚀 NEW: Filter games by season when season selection changes
  useEffect(() => {
    const filteredGames = filterGamesBySeason(gamesData, selectedSeason);
    devLog("Season filtered games:", {
      originalCount: gamesData.length,
      filteredCount: filteredGames.length,
      selectedSeason,
    });
    setSeasonFilteredGames(filteredGames);
  }, [gamesData, selectedSeason]);

  // 🚀 UPDATED: Use season-filtered games for chart updates
  useEffect(() => {
    if (seasonFilteredGames.length > 0) {
      updateChartsAndInsights();
    }
  }, [
    viewMode,
    selectedPlayerId,
    matchesFilter,
    seasonFilteredGames, // 🚀 CHANGED: Use season-filtered games
    isDateRange,
    startDate,
    endDate,
    selectedSeason,
  ]);

  // ------------------------- API Calls ------------------------- //
  const fetchTeams = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}`, {
        operationType: "fetch_teams_by_manager_id",
        data: { manager_id: coach_id },
      });

      const teamsData = response.data.body
        ? JSON.parse(response.data.body).teams
        : [];
      setTeams(teamsData);

      // If only one team, auto-select
      if (teamsData.length === 1) {
        setSelectedTeamId(teamsData[0].team_id);
      }
    } catch (err) {
      console.error("Error fetching teams:", err);
      setError("Failed to load teams.");
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async (teamId) => {
    setLoading(true);
    try {
      devLog("🔵 API request sent. Awaiting response...");
      const response = await axios.post(`${API_URL}`, {
        operationType: "get_player_stats",
        data: { team_id: teamId },
      });

      devLog("🟢 Raw API Response:", response.data);

      if (!response.data.body) {
        console.error("❌ API response is missing `body`:", response.data);
        return;
      }

      devLog("🟡 Raw API Body (before parsing):", response.data.body);

      let matchesData;
      try {
        matchesData = JSON.parse(response.data.body);
      } catch (parseError) {
        console.error("❌ Failed to parse `body` as JSON:", parseError);
        return;
      }

      devLog("🟢 Parsed Matches Data:", matchesData);

      if (!Array.isArray(matchesData)) {
        console.error("❌ Parsed data is not an array:", matchesData);
        return;
      }

      const matchIds = matchesData.map((match) => match.match_id);
      devLog("✅ Extracted Match IDs:", matchIds);

      if (matchIds.some((id) => id === undefined)) {
        console.warn("⚠️ Some match IDs are undefined. Check API response.");
      }

      processGameData(matchesData);
    } catch (err) {
      console.error("❌ Error fetching data:", err);
      setError("Failed to fetch data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ------------------------- Process & Aggregate ------------------------- //
  const processGameData = (matchesData) => {
    if (!Array.isArray(matchesData)) {
      setError("Invalid data format received from the server.");
      return;
    }

    const gameDb = {};
    const playerDb = {};

    matchesData.forEach((match) => {
      const matchId = match.match_id;
      const oppositionScore = match.opposition_score || 0;

      gameDb[matchId] = {
        match_date: match.match_date,
        opposition_name: match.opposition_name || "Unknown Opponent",
        opposition_score: oppositionScore,
        my_score: match.my_score || 0,
        players: {},
      };

      match.player_stats.forEach((p) => {
        const stats = {
          goals: p.goals,
          assists: p.assists,
          shots_on_target: p.shots_on_target,
          shots_off_target: p.shots_off_target,
          saves: p.saves,
          defensive_actions: p.defensive_actions || 0,
          status: p.status || "unknown",
        };

        gameDb[matchId].players[p.player_id] = stats;

        if (!playerDb[p.player_id]) {
          playerDb[p.player_id] = {
            id: p.player_id,
            name: p.name,
            gamesPlayed: 0,
            totalStats: { ...stats },
            perMatch: [],
          };
        } else {
          playerDb[p.player_id].totalStats.goals += stats.goals;
          playerDb[p.player_id].totalStats.assists += stats.assists;
          playerDb[p.player_id].totalStats.shots_on_target +=
            stats.shots_on_target;
          playerDb[p.player_id].totalStats.shots_off_target +=
            stats.shots_off_target;
          playerDb[p.player_id].totalStats.saves += stats.saves;
        }

        playerDb[p.player_id].gamesPlayed += 1;

        playerDb[p.player_id].perMatch.push({
          match_date: match.match_date,
          opposition: match.opposition_name,
          goals: stats.goals,
          assists: stats.assists,
          shots_on_target: stats.shots_on_target,
          shots_off_target: stats.shots_off_target,
          saves: stats.saves,
        });
      });
    });

    const sortedGames = Object.values(gameDb).sort(
      (a, b) => new Date(b.match_date) - new Date(a.match_date)
    );

    setGamesData(sortedGames);
    setAggregatedPlayerStats(playerDb);
    setPlayers(
      Object.values(playerDb).map((p) => ({ id: p.id, name: p.name }))
    );
  };

  // 🚀 UPDATED: Use season-filtered games for chart updates
  const updateChartsAndInsights = () => {
    // 🚀 UPDATED: Start with season-filtered games instead of all games
    let filteredGames = [...seasonFilteredGames];
    let gamesCount = filteredGames.length;

    // Apply additional filters on top of season filter
    if (isDateRange) {
      if (startDate) {
        filteredGames = filteredGames.filter(
          (game) => new Date(game.match_date) >= startDate
        );
      }
      if (endDate) {
        filteredGames = filteredGames.filter(
          (game) => new Date(game.match_date) <= endDate
        );
      }
      gamesCount = filteredGames.length;
    } else if (matchesFilter) {
      filteredGames = filteredGames.slice(0, matchesFilter);
      gamesCount = filteredGames.length;
    }

    if (viewMode === "team") {
      setChartData(generateTeamChartData(filteredGames));
      const teamIns = generateTeamInsights(filteredGames);
      setTeamInsights({ ...teamIns, gamesCount });
      setPlayerInsights({});
      calculatePieAndBarStatsTeam(filteredGames);
      setTopDefensivePlayers(generateTopDefensiveActions(filteredGames));

      setTopGoalScorers(generateTopScorers(filteredGames));
      setTopAssistProviders(generateTopAssists(filteredGames));
    } else if (viewMode === "individual" && selectedPlayerId) {
      setChartData(generatePlayerChartData(filteredGames, selectedPlayerId));
      const playerIns = generatePlayerInsights(selectedPlayerId, filteredGames);
      setPlayerInsights(playerIns);
      setTeamInsights({});
      calculatePieAndBarStatsIndividual(filteredGames, selectedPlayerId);

      setGoalsAssistsPieData(playerIns.goalsAssistsPieData || []);
    }
  };

  // ----------------------TEAM: Progress Bars---------------------- //
  const calculatePieAndBarStatsTeam = (recentGames) => {
    let totalSaves = 0;
    let totalGoalsConceded = 0;
    let totalShotsOn = 0;
    let totalShotsOff = 0;
    let totalGoals = 0;
    let totalAssists = 0;

    recentGames.forEach((game) => {
      totalGoalsConceded += game.opposition_score || 0;
      Object.values(game.players).forEach((stats) => {
        totalSaves += stats.saves;
        totalShotsOn += stats.shots_on_target;
        totalShotsOff += stats.shots_off_target;
        totalGoals += stats.goals;
        totalAssists += stats.assists;
      });
    });

    const total = totalSaves + totalGoalsConceded;
    const savesPercent = total > 0 ? (totalSaves / total) * 100 : 0;
    const goalsConcededPercent =
      total > 0 ? (totalGoalsConceded / total) * 100 : 0;

    setSavesPercent(savesPercent);
    setGoalsConcededPercent(goalsConcededPercent);

    setSavesCount(totalSaves);
    setGoalsConceded(totalGoalsConceded);

    const totalShots = totalShotsOn + totalShotsOff;
    const onPct = totalShots > 0 ? (totalShotsOn / totalShots) * 100 : 0;
    const offPct = totalShots > 0 ? (totalShotsOff / totalShots) * 100 : 0;
    setShotsOnPercent(onPct);
    setShotsOnCount(totalShotsOn);
    setShotsOffPercent(offPct);
    setShotsOffCount(totalShotsOff);

    const gaTotal = totalGoals + totalAssists;
    const goalsPct = gaTotal > 0 ? (totalGoals / gaTotal) * 100 : 0;
    const assistsPct = gaTotal > 0 ? (totalAssists / gaTotal) * 100 : 0;
    setGoalsPercent(goalsPct);
    setGoalsCount(totalGoals);
    setAssistsPercent(assistsPct);
    setAssistsCount(totalAssists);
  };

  const generateTopDefensiveActions = (recentGames) => {
    const defMap = {};

    recentGames.forEach((game) => {
      Object.entries(game.players).forEach(([playerId, stats]) => {
        if (!defMap[playerId]) {
          const playerName = aggregatedPlayerStats[playerId]?.name || "Unknown";
          defMap[playerId] = {
            id: playerId,
            name: playerName,
            defensive_actions: 0,
          };
        }
        defMap[playerId].defensive_actions += stats.defensive_actions || 0;
      });
    });

    const defArray = Object.values(defMap);
    defArray.sort((a, b) => b.defensive_actions - a.defensive_actions);
    return defArray.slice(0, 5);
  };

  // ----------------------INDIVIDUAL: Progress Bars---------------------- //
  const calculatePieAndBarStatsIndividual = (recentGames, playerId) => {
    let totalSaves = 0;
    let totalGoalsConceded = 0;
    let totalShotsOn = 0;
    let totalShotsOff = 0;
    let totalGoals = 0;
    let totalAssists = 0;

    devLog("Calculating stats for player:", playerId);

    recentGames.forEach((game) => {
      const playerStats = game.players[playerId];
      if (playerStats) {
        totalGoalsConceded += game.opposition_score || 0;
        totalSaves += playerStats.saves || 0;
        totalShotsOn += playerStats.shots_on_target || 0;
        totalShotsOff += playerStats.shots_off_target || 0;
        totalGoals += playerStats.goals || 0;
        totalAssists += playerStats.assists || 0;
      }
    });

    const total = totalSaves + totalGoalsConceded;
    const savesPercent = total > 0 ? (totalSaves / total) * 100 : 0;
    const goalsConcededPercent =
      total > 0 ? (totalGoalsConceded / total) * 100 : 0;

    const totalShots = totalShotsOn + totalShotsOff;
    const shotsOnPercent =
      totalShots > 0 ? (totalShotsOn / totalShots) * 100 : 0;
    const shotsOffPercent =
      totalShots > 0 ? (totalShotsOff / totalShots) * 100 : 0;

    const goalsAssistsTotal = totalGoals + totalAssists;
    const goalsPercent =
      goalsAssistsTotal > 0 ? (totalGoals / goalsAssistsTotal) * 100 : 0;
    const assistsPercent =
      goalsAssistsTotal > 0 ? (totalAssists / goalsAssistsTotal) * 100 : 0;

    setSavesPercent(savesPercent);
    setGoalsConcededPercent(goalsConcededPercent);
    setSavesCount(totalSaves);
    setGoalsConceded(totalGoalsConceded);

    setShotsOnPercent(shotsOnPercent);
    setShotsOnCount(totalShotsOn);
    setShotsOffPercent(shotsOffPercent);
    setShotsOffCount(totalShotsOff);

    setGoalsPercent(goalsPercent);
    setGoalsCount(totalGoals);
    setAssistsPercent(assistsPercent);
    setAssistsCount(totalAssists);
  };

  // ---------------------- Chart Data Generators ---------------------- //
  const generateTeamChartData = (games) => {
    return games.map((game) => {
      const gameStats = {
        match_date: game.match_date,
        opposition_name: game.opposition_name,
        goals: 0,
        assists: 0,
        shots_on_target: 0,
        shots_off_target: 0,
        saves: 0,
      };
      Object.values(game.players).forEach((stats) => {
        gameStats.goals += stats.goals;
        gameStats.assists += stats.assists;
        gameStats.shots_on_target += stats.shots_on_target;
        gameStats.shots_off_target += stats.shots_off_target;
        gameStats.saves += stats.saves;
      });
      return gameStats;
    });
  };

  const generatePlayerChartData = (games, playerId) => {
    return games
      .filter((game) => game.players[playerId])
      .map((game) => {
        const pStats = game.players[playerId];
        return {
          match_date: game.match_date,
          opposition_name: game.opposition_name,
          goals: pStats.goals,
          assists: pStats.assists,
          shots_on_target: pStats.shots_on_target,
          shots_off_target: pStats.shots_off_target,
          saves: pStats.saves,
        };
      });
  };

  // ---------------------- Insights ---------------------- //
  const generateTeamInsights = (games) => {
    if (games.length === 0) return { gamesCount: 0 };

    let totalGoals = 0;
    let totalAssists = 0;
    let totalShotsOn = 0;
    let totalShotsOff = 0;
    let totalShots = 0;
    let totalDefensiveActions = 0;
    let totalSaves = 0;
    let totalOppScore = 0;

    games.forEach((game) => {
      totalOppScore += game.opposition_score || 0;
      totalGoals += game.my_score || 0;

      Object.values(game.players).forEach((stats) => {
        totalAssists += stats.assists;
        totalShotsOn += stats.shots_on_target;
        totalShotsOff += stats.shots_off_target;
        totalSaves += stats.saves;
        totalDefensiveActions += stats.defensive_actions || 0;
      });
    });

    totalShots = totalShotsOn + totalShotsOff;

    const gamesCount = games.length;
    const goalsConcededPerGame = (totalOppScore / gamesCount).toFixed(2);

    return {
      gamesCount,
      goalsPerGame: (totalGoals / gamesCount).toFixed(2),
      assistsPerGame: (totalAssists / gamesCount).toFixed(2),
      goalsConcededPerGame: (totalOppScore / gamesCount).toFixed(2),
      shotAccuracy:
        totalShots > 0
          ? ((totalShotsOn / totalShots) * 100).toFixed(1) + "%"
          : "0%",
      savesPerGame: (totalSaves / gamesCount).toFixed(2),
      shotConversionRate:
        totalShots > 0
          ? ((totalGoals / totalShots) * 100).toFixed(1) + "%"
          : "0%",
      goalsConceded: totalOppScore,
      totalGoals,
      totalShots,
      defensiveActionsPerGame: (totalDefensiveActions / gamesCount).toFixed(2),
    };
  };

  const generatePlayerInsights = (playerId, recentGames) => {
    devLog(`🟠 Generating insights for Player ID: ${playerId}`);

    const playerData = aggregatedPlayerStats[playerId];
    if (!playerData) {
      console.warn("⚠️ Player data not found for ID:", playerId);
      return { gamesCount: 0 };
    }

    devLog("✅ Found Player Data:", playerData);

    let totalGoals = 0;
    let totalAssists = 0;
    let totalShotsOn = 0;
    let totalShotsOff = 0;
    let totalSaves = 0;
    let totalTeamGoals = 0;

    let gamesPlayed = 0;

    recentGames.forEach((game) => {
      const pStats = game.players[playerId];

      if (pStats) {
        gamesPlayed++;
        totalGoals += pStats.goals;
        totalAssists += pStats.assists;
        totalShotsOn += pStats.shots_on_target;
        totalShotsOff += pStats.shots_off_target;
        totalSaves += pStats.saves;
      }

      Object.values(game.players).forEach((stats) => {
        totalTeamGoals += stats.goals;
      });
    });

    devLog(`🟢 Player ${playerId} played in ${gamesPlayed} games`);
    devLog(`🔵 Total team goals: ${totalTeamGoals}`);
    devLog(`🔴 Player's total goals: ${totalGoals}, Assists: ${totalAssists}`);

    let goalsInvolvements =
      totalTeamGoals > 0
        ? ((totalGoals + totalAssists) / totalTeamGoals) * 100
        : 0;

    let rawGoalInvolvements = `${totalGoals + totalAssists}/${totalTeamGoals}`;

    const goalsAssistsPieData = [
      { name: "Goals", value: totalGoals },
      { name: "Assists", value: totalAssists },
    ];

    return {
      gamesCount: gamesPlayed,
      goalsPerGame:
        gamesPlayed > 0 ? (totalGoals / gamesPlayed).toFixed(2) : "0.00",

      assistsPerGame:
        gamesPlayed > 0 ? (totalAssists / gamesPlayed).toFixed(2) : "0.00",
      shotAccuracy:
        totalShotsOn + totalShotsOff > 0
          ? ((totalShotsOn / (totalShotsOn + totalShotsOff)) * 100).toFixed(1) +
            "%"
          : "0%",
      savesPerGame:
        gamesPlayed > 0 ? (totalSaves / gamesPlayed).toFixed(2) : "0.00",
      shotConversionRate:
        totalShotsOn + totalShotsOff > 0
          ? ((totalGoals / (totalShotsOn + totalShotsOff)) * 100).toFixed(1) +
            "%"
          : "0%",
      defensiveActionsPerGame:
        gamesPlayed > 0
          ? (
              recentGames.reduce((acc, game) => {
                const stats = game.players[playerId];
                return acc + (stats ? stats.defensive_actions || 0 : 0);
              }, 0) / gamesPlayed
            ).toFixed(2)
          : "0.00",
      goalsInvolvements: `${goalsInvolvements.toFixed(1)}%`,
      rawGoalInvolvements,
      goalsAssistsPieData,
    };
  };

  // ---------------------- Top Scorers and Assists Generators ---------------------- //
  const generateTopScorers = (recentGames) => {
    const scorerMap = {};

    recentGames.forEach((game) => {
      Object.entries(game.players).forEach(([playerId, stats]) => {
        if (!scorerMap[playerId]) {
          const playerName = aggregatedPlayerStats[playerId]?.name || "Unknown";
          scorerMap[playerId] = {
            id: playerId,
            name: playerName,
            goals: 0,
          };
        }
        scorerMap[playerId].goals += stats.goals;
      });
    });

    const scorersArray = Object.values(scorerMap);
    scorersArray.sort((a, b) => b.goals - a.goals);
    return scorersArray.slice(0, 5);
  };

  const generateTopAssists = (recentGames) => {
    const assistMap = {};

    recentGames.forEach((game) => {
      Object.entries(game.players).forEach(([playerId, stats]) => {
        if (!assistMap[playerId]) {
          const playerName = aggregatedPlayerStats[playerId]?.name || "Unknown";
          assistMap[playerId] = {
            id: playerId,
            name: playerName,
            assists: 0,
          };
        }
        assistMap[playerId].assists += stats.assists;
      });
    });

    const assistsArray = Object.values(assistMap);
    assistsArray.sort((a, b) => b.assists - a.assists);
    return assistsArray.slice(0, 5);
  };

  // ---------------------- Filter & Excel (Multiple Sheets) ---------------------- //
  const handleFilterChange = (value) => {
    if (value === "between_dates") {
      setIsDateRange(true);
      setMatchesFilter(null);
      setStartDate(null);
      setEndDate(null);
      setSelectedGameIds([]);
    } else if (value === "select_games") {
      setIsDateRange(false);
      setMatchesFilter("select_games");
      setStartDate(null);
      setEndDate(null);
    } else {
      setIsDateRange(false);
      setMatchesFilter(parseInt(value, 10));
      setStartDate(null);
      setEndDate(null);
      setSelectedGameIds([]);
    }
  };

  // 🚀 NEW: Season filter change handler
  const handleSeasonChange = (value) => {
    devLog("🔧 Season changed to:", value);
    setSelectedSeason(value);
    // Reset other filters when season changes
    setMatchesFilter(1);
    setIsDateRange(false);
    setStartDate(null);
    setEndDate(null);
  };

  // 🚀 UPDATED: Excel export to include season info
  const downloadExcel = () => {
    const workbook = XLSX.utils.book_new();

    // Determine which data to export
    const dataToExport =
      selectedSeason === "all" ? gamesData : seasonFilteredGames;

    // 🚀 UPDATED: Team Summary Sheet with season info
    const teamSummaryData = [];
    teamSummaryData.push({
      Key: "Team Statistics Summary",
      Value: "",
    });
    teamSummaryData.push({});

    // Add season info if specific season selected
    if (selectedSeason !== "all") {
      const seasonInfo = availableSeasons.find(
        (s) => s.value === selectedSeason
      );
      teamSummaryData.push({
        Key: "Season",
        Value: `${seasonInfo?.label} Season`,
      });
      teamSummaryData.push({});
    }

    teamSummaryData.push({
      Key: "Total Games Played",
      Value: dataToExport.length,
    });
    teamSummaryData.push({
      Key: "Total Goals Scored",
      Value: dataToExport.reduce((sum, game) => sum + (game.my_score || 0), 0),
    });
    teamSummaryData.push({
      Key: "Total Goals Conceded",
      Value: dataToExport.reduce(
        (sum, game) => sum + (game.opposition_score || 0),
        0
      ),
    });

    const teamSummaryWS = XLSX.utils.json_to_sheet(teamSummaryData, {
      skipHeader: true,
    });
    XLSX.utils.book_append_sheet(workbook, teamSummaryWS, "Team Summary");

    // "All Games" sheet
    const allGamesSheetData = [];
    allGamesSheetData.push({
      MatchDate: "Date",
      Opposition: "Opposition",
      Goals: "Goals",
      Assists: "Assists",
      ShotsOn: "Shots On",
      ShotsOff: "Shots Off",
      Saves: "Saves",
      GoalsConceded: "Goals Conceded",
      Season: "Season", // 🚀 NEW: Add season column
    });

    const fullTeamChartData = generateTeamChartData(dataToExport);

    fullTeamChartData.forEach((matchItem) => {
      const foundGame = dataToExport.find(
        (g) =>
          g.match_date === matchItem.match_date &&
          g.opposition_name === matchItem.opposition_name
      );
      const goalsConceded = foundGame ? foundGame.opposition_score : 0;
      const gameSeason = getSeasonFromDate(matchItem.match_date); // 🚀 NEW: Get season for each game

      allGamesSheetData.push({
        MatchDate: matchItem.match_date,
        Opposition: matchItem.opposition_name,
        Goals: matchItem.goals,
        Assists: matchItem.assists,
        ShotsOn: matchItem.shots_on_target,
        ShotsOff: matchItem.shots_off_target,
        Saves: matchItem.saves,
        GoalsConceded: goalsConceded,
        Season: gameSeason.label, // 🚀 NEW: Add season info
      });
    });

    const allGamesWS = XLSX.utils.json_to_sheet(allGamesSheetData);
    XLSX.utils.book_append_sheet(workbook, allGamesWS, "All Games");

    // Player Statistics Sheet (aggregate from selected data)
    const playerStatsData = [];
    playerStatsData.push({
      PlayerName: "Player Name",
      GamesPlayed: "Games Played",
      Goals: "Goals",
      Assists: "Assists",
      ShotsOn: "Shots On Target",
      ShotsOff: "Shots Off Target",
      Saves: "Saves",
      DefensiveActions: "Defensive Actions",
    });

    // Calculate stats from filtered data
    const playerStatsFromFiltered = {};
    dataToExport.forEach((game) => {
      Object.entries(game.players).forEach(([playerId, stats]) => {
        if (!playerStatsFromFiltered[playerId]) {
          playerStatsFromFiltered[playerId] = {
            name: aggregatedPlayerStats[playerId]?.name || "Unknown Player",
            gamesPlayed: 0,
            goals: 0,
            assists: 0,
            shots_on_target: 0,
            shots_off_target: 0,
            saves: 0,
            defensive_actions: 0,
          };
        }

        playerStatsFromFiltered[playerId].gamesPlayed += 1;
        playerStatsFromFiltered[playerId].goals += stats.goals || 0;
        playerStatsFromFiltered[playerId].assists += stats.assists || 0;
        playerStatsFromFiltered[playerId].shots_on_target +=
          stats.shots_on_target || 0;
        playerStatsFromFiltered[playerId].shots_off_target +=
          stats.shots_off_target || 0;
        playerStatsFromFiltered[playerId].saves += stats.saves || 0;
        playerStatsFromFiltered[playerId].defensive_actions +=
          stats.defensive_actions || 0;
      });
    });

    Object.values(playerStatsFromFiltered).forEach((player) => {
      playerStatsData.push({
        PlayerName: player.name,
        GamesPlayed: player.gamesPlayed,
        Goals: player.goals,
        Assists: player.assists,
        ShotsOn: player.shots_on_target,
        ShotsOff: player.shots_off_target,
        Saves: player.saves,
        DefensiveActions: player.defensive_actions,
      });
    });

    const playerStatsWS = XLSX.utils.json_to_sheet(playerStatsData);
    XLSX.utils.book_append_sheet(workbook, playerStatsWS, "Player Statistics");

    // 🚀 UPDATED: Include season in filename
    const timestamp = new Date().toISOString().slice(0, 10);
    const seasonSuffix =
      selectedSeason !== "all" ? `_${selectedSeason.replace("-", "_")}` : "";
    const filename = `Team_Stats_${timestamp}${seasonSuffix}.xlsx`;

    XLSX.writeFile(workbook, filename);
  };

  // ---------------------- Format Helpers ---------------------- //
  const formatLabel = (label) => {
    return label
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase());
  };

  const progressBarContainerStyle = {
    width: "100%",
    height: "12px",
    backgroundColor: "#e0e0e0",
    borderRadius: "6px",
    overflow: "hidden",
    marginBottom: "8px",
  };

  const progressFillStyle = (pct, color) => ({
    width: `${pct}%`,
    height: "100%",
    backgroundColor: color,
  });

  // ---------------------- Render ---------------------- //
  if (loading) {
    return (
      <Box
        minHeight="60vh"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <div className="football-spinner"></div>
      </Box>
    );
  }

  // Determine current insights based on view mode
  const currentInsights = viewMode === "team" ? teamInsights : playerInsights;

  return (
    <Box sx={{ maxWidth: 1200, margin: "0 auto", p: 1, marginTop: 4 }}>
      <Typography variant="h5" gutterBottom>
        Player/Team Stats
      </Typography>

      {error && (
        <Typography color="error" variant="body1" gutterBottom>
          {error}
        </Typography>
      )}

      {/* 🚀 UPDATED: Filters Section with Season Filter */}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        {/* Matches Filter Dropdown */}
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Filter Matches</InputLabel>
            <Select
              label="Filter Matches"
              value={matchesFilter || "between_dates"}
              onChange={(e) => handleFilterChange(e.target.value)}
            >
              <MenuItem value={1}>Last Game</MenuItem>
              <MenuItem value={5}>Last 5 Matches</MenuItem>
              <MenuItem value={10}>Last 10 Matches</MenuItem>
              <MenuItem value={15}>Last 15 Matches</MenuItem>
              <MenuItem value={20}>Last 20 Matches</MenuItem>
              <MenuItem value="between_dates">Between dates...</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* 🚀 NEW: Season Filter */}
        {availableSeasons.length > 0 && (
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Select Season</InputLabel>
              <Select
                label="Select Season"
                value={selectedSeason}
                onChange={(e) => handleSeasonChange(e.target.value)}
              >
                <MenuItem value="all">All Seasons</MenuItem>
                {availableSeasons.map((season) => (
                  <MenuItem key={season.value} value={season.value}>
                    {season.label} Season
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* View Mode Dropdown */}
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>View Mode</InputLabel>
            <Select
              label="View Mode"
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
            >
              <MenuItem value="team">Team Stats</MenuItem>
              <MenuItem value="individual">Individual Stats</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Player Selection Dropdown */}
        {viewMode === "individual" && (
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Select Player</InputLabel>
              <Select
                label="Select Player"
                value={selectedPlayerId}
                onChange={(e) => setSelectedPlayerId(e.target.value)}
              >
                <MenuItem value="">
                  <em>Select a Player</em>
                </MenuItem>
                {Object.entries(aggregatedPlayerStats).map(([id, p]) => (
                  <MenuItem key={id} value={id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Excel Download Button */}
        <Grid item xs={12} sm={6} md={3} display="flex" alignItems="center">
          <Button
            variant="contained"
            onClick={downloadExcel}
            fullWidth
            size="small"
          >
            Download as Excel
          </Button>
        </Grid>
      </Grid>

      {/* 🚀 NEW: Season Info Display */}
      {selectedSeason !== "all" && availableSeasons.length > 0 && (
        <Card variant="outlined" sx={{ mt: 2, backgroundColor: "#f8f9fa" }}>
          <CardContent sx={{ py: 1.5 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Chip
                label="📅"
                size="small"
                sx={{ backgroundColor: "#e3f2fd", fontSize: "14px" }}
              />
              <Typography variant="body2" color="primary" fontWeight="500">
                {
                  availableSeasons.find((s) => s.value === selectedSeason)
                    ?.label
                }{" "}
                Season
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ({seasonFilteredGames.length} matches available)
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Date Pickers Section */}
      {isDateRange && (
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {/* Start Date Picker */}
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              placeholderText="Start Date"
              isClearable
              customInput={
                <TextField
                  fullWidth
                  size="small"
                  label="Start Date"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              }
            />
          </Grid>

          {/* End Date Picker */}
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              placeholderText="End Date"
              isClearable
              customInput={
                <TextField
                  fullWidth
                  size="small"
                  label="End Date"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              }
            />
          </Grid>

          {/* Clear Date Filters Button */}
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              size="small"
              onClick={() => {
                setStartDate(null);
                setEndDate(null);
              }}
              sx={{ height: "56px" }}
            >
              Clear Date Filters
            </Button>
          </Grid>
        </Grid>
      )}

      {/* Combined Insight Card */}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Accumulated Stats
              </Typography>
              <Grid container spacing={2}>
                {/* Number of Games */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center" sx={{ mb: 0 }}>
                    <Typography variant="h6" gutterBottom>
                      Number of Games
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {currentInsights.gamesCount ?? "0"}
                    </Typography>
                  </Box>
                </Grid>
                {/* Goals per Game */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h6" gutterBottom>
                      Goals per Game
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {currentInsights.goalsPerGame ?? "0.00"}
                    </Typography>
                    <Typography variant="body2">Goals / Game</Typography>
                  </Box>
                </Grid>

                {/* Assists per Game */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h6" gutterBottom>
                      Assists per Game
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {currentInsights.assistsPerGame ?? "0.00"}
                    </Typography>
                    <Typography variant="body2">Assists / Game</Typography>
                  </Box>
                </Grid>

                {/* Shot Conversion */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h6" gutterBottom>
                      Shot Conversion
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {currentInsights.shotConversionRate ?? "0%"}
                    </Typography>
                    <Typography variant="body2">Shots → Goals</Typography>
                  </Box>
                </Grid>

                {/* Shot Accuracy */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h6" gutterBottom>
                      Shot Accuracy
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {currentInsights.shotAccuracy ?? "0%"}
                    </Typography>
                    <Typography variant="body2">% On Target</Typography>
                  </Box>
                </Grid>
                {/* Defensive Actions per Game */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h6" gutterBottom>
                      Defensive Actions
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {currentInsights.defensiveActionsPerGame ?? "0.00"}
                    </Typography>
                    <Typography variant="body2">Actions / Game</Typography>
                  </Box>
                </Grid>
                {/* Goals Conceded per Game */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h6" gutterBottom>
                      Goals Conceded / Game
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {currentInsights.goalsConcededPerGame ?? "0.00"}
                    </Typography>
                    <Typography variant="body2">Avg. Conceded</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Progress Bars and Charts */}
      <Grid container spacing={2} mt={2}>
        {/* Saves vs. Goals Conceded */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Saves vs. Goals Conceded
              </Typography>
              <Typography variant="body2" gutterBottom>
                Saves: {savesCount} ({savesPercent.toFixed(1)}%) | Goals
                Conceded: {goalsConceded} ({goalsConcededPercent.toFixed(1)}%)
              </Typography>
              <Box>
                {/* Saves Progress Bar */}
                <Typography variant="subtitle2" gutterBottom>
                  Saves
                </Typography>
                <Box sx={progressBarContainerStyle}>
                  <Box
                    sx={progressFillStyle(savesPercent, "#8884d8")}
                    title={`Saves: ${savesPercent.toFixed(1)}%`}
                  />
                </Box>

                {/* Goals Conceded Progress Bar */}
                <Typography variant="subtitle2" gutterBottom>
                  Goals Conceded
                </Typography>
                <Box sx={progressBarContainerStyle}>
                  <Box
                    sx={progressFillStyle(goalsConcededPercent, "#82ca9d")}
                    title={`Goals Conceded: ${goalsConcededPercent.toFixed(
                      1
                    )}%`}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Shots On vs. Shots Off Progress Bars */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Shots On vs. Shots Off
              </Typography>
              <Typography variant="body2" gutterBottom>
                On Target: {shotsOnCount} ({shotsOnPercent.toFixed(1)}%) | Off
                Target: {shotsOffCount} ({shotsOffPercent.toFixed(1)}%)
              </Typography>
              <Box>
                {/* Shots On Target Progress Bar */}
                <Typography variant="subtitle2" gutterBottom>
                  Shots On Target
                </Typography>
                <Box sx={progressBarContainerStyle}>
                  <Box
                    sx={progressFillStyle(shotsOnPercent, "#8884d8")}
                    title={`Shots On Target: ${shotsOnPercent.toFixed(1)}%`}
                  />
                </Box>

                {/* Shots Off Target Progress Bar */}
                <Typography variant="subtitle2" gutterBottom>
                  Shots Off Target
                </Typography>
                <Box sx={progressBarContainerStyle}>
                  <Box
                    sx={progressFillStyle(shotsOffPercent, "#82ca9d")}
                    title={`Shots Off Target: ${shotsOffPercent.toFixed(1)}%`}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Goals vs. Assists Progress Bars */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Goals vs. Assists
              </Typography>
              <Typography variant="body2" gutterBottom>
                Goals: {goalsCount} ({goalsPercent.toFixed(1)}%) | Assists:{" "}
                {assistsCount} ({assistsPercent.toFixed(1)}%)
              </Typography>
              <Box>
                {/* Goals Progress Bar */}
                <Typography variant="subtitle2" gutterBottom>
                  Goals
                </Typography>
                <Box sx={progressBarContainerStyle}>
                  <Box
                    sx={progressFillStyle(goalsPercent, "#8884d8")}
                    title={`Goals: ${goalsPercent.toFixed(1)}%`}
                  />
                </Box>

                {/* Assists Progress Bar */}
                <Typography variant="subtitle2" gutterBottom>
                  Assists
                </Typography>
                <Box sx={progressBarContainerStyle}>
                  <Box
                    sx={progressFillStyle(assistsPercent, "#82ca9d")}
                    title={`Assists: ${assistsPercent.toFixed(1)}%`}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Goal Conversion */}
      <Card variant="outlined" sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Goal Conversion
          </Typography>
          {(() => {
            const totalShots = shotsOnCount + shotsOffCount;
            const conversionRate =
              totalShots > 0 ? (goalsCount / totalShots) * 100 : 0;

            return (
              <Box
                display="flex"
                justifyContent="space-around"
                alignItems="center"
                mt={2}
              >
                <Box textAlign="center">
                  <Typography variant="h4" color="primary">
                    {conversionRate.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2">Conversion Rate</Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h4" color="secondary">
                    {goalsCount}/{totalShots}
                  </Typography>
                  <Typography variant="body2">Goals / Shots</Typography>
                </Box>
              </Box>
            );
          })()}
        </CardContent>
      </Card>

      {/* Top 5 Goal Scorers and Assist Providers */}
      {viewMode === "team" && (
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Top 5 Goal Scorers & Assist Providers
            </Typography>
            <Grid container spacing={2}>
              {/* Top 5 Goal Scorers */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Top 5 Goal Scorers
                </Typography>
                <Box component="ul" sx={{ listStyleType: "none", padding: 0 }}>
                  {topGoalScorers.length > 0 ? (
                    topGoalScorers.map((player, index) => (
                      <li key={player.id}>
                        <Typography variant="body2">
                          {index + 1}. {player.name} - {player.goals} Goals
                        </Typography>
                      </li>
                    ))
                  ) : (
                    <Typography variant="body2">No data available.</Typography>
                  )}
                </Box>
              </Grid>

              {/* Top 5 Assist Providers */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Top 5 Assist Providers
                </Typography>
                <Box component="ul" sx={{ listStyleType: "none", padding: 0 }}>
                  {topAssistProviders.length > 0 ? (
                    topAssistProviders.map((player, index) => (
                      <li key={player.id}>
                        <Typography variant="body2">
                          {index + 1}. {player.name} - {player.assists} Assists
                        </Typography>
                      </li>
                    ))
                  ) : (
                    <Typography variant="body2">No data available.</Typography>
                  )}
                </Box>
              </Grid>
              {/* Top 5 Defensive Players */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Top 5 Defensive Players
                </Typography>
                <Box component="ul" sx={{ listStyleType: "none", padding: 0 }}>
                  {topDefensivePlayers.length > 0 ? (
                    topDefensivePlayers.map((player, index) => (
                      <li key={player.id}>
                        <Typography variant="body2">
                          {index + 1}. {player.name} -{" "}
                          {player.defensive_actions} Defensive Actions
                        </Typography>
                      </li>
                    ))
                  ) : (
                    <Typography variant="body2">No data available.</Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Goal Involvement (Individual Only) */}
      {viewMode === "individual" && (
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Goal Involvement
            </Typography>
            {(() => {
              const giPct = currentInsights.goalsInvolvements || "0%";
              const rawGI = currentInsights.rawGoalInvolvements || "0/0";
              const pieData =
                goalsAssistsPieData.length > 0
                  ? goalsAssistsPieData
                  : [
                      { name: "Goals", value: 0 },
                      { name: "Assists", value: 0 },
                    ];

              return (
                <Box
                  display="flex"
                  flexDirection={{ xs: "column", sm: "row" }}
                  justifyContent="space-around"
                  alignItems="center"
                  mt={2}
                >
                  {/* Goals Involvement % */}
                  <Box textAlign="center" sx={{ mb: { xs: 2, sm: 0 } }}>
                    <Typography variant="h4" color="primary">
                      {giPct}
                    </Typography>
                    <Typography variant="body2">
                      Percent of Team's Goals
                    </Typography>
                  </Box>

                  {/* Raw Goal Involvement */}
                  <Box textAlign="center">
                    <Typography variant="h4" color="secondary">
                      {rawGI}
                    </Typography>
                    <Typography variant="body2">
                      (Goals + Assists) / Team Goals
                    </Typography>
                  </Box>

                  {/* Goals vs. Assists Pie Chart */}
                  <Box sx={{ mt: { xs: 2, sm: 0 }, width: 150, height: 150 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={pieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={60}
                          label={({ name, percent }) =>
                            `${name}: ${(percent * 100).toFixed(0)}%`
                          }
                        >
                          {pieData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={pieColors[index % pieColors.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name) => `${value} ${name}`}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* No data message */}
      {!loading &&
        (selectedSeason === "all"
          ? gamesData.length
          : seasonFilteredGames.length) === 0 &&
        selectedTeamId &&
        !error && (
          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
              <Typography
                variant="body1"
                color="text.secondary"
                textAlign="center"
              >
                {selectedSeason === "all"
                  ? "No match data found for this team. Play some matches first!"
                  : `No match data found for ${
                      availableSeasons.find((s) => s.value === selectedSeason)
                        ?.label
                    } season.`}
              </Typography>
            </CardContent>
          </Card>
        )}
    </Box>
  );
};

export default StatsPage;
